<!-- ── Hero ── -->
<section class="hero">
  <div class="hero-inner">
    <div class="hero-left">
      <h1>Manage Users</h1>
      <p class="hero-sub">Assign roles, edit info, and manage user access</p>
    </div>
    <div class="hero-right">
      <div class="hero-kpi">
        <mat-icon>group</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ visibleUsers.length }}</span>
          <span class="kpi-label">Users</span>
        </div>
      </div>
      <div class="hero-kpi">
        <mat-icon>admin_panel_settings</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ allRoles.length }}</span>
          <span class="kpi-label">Roles</span>
        </div>
      </div>
    </div>
  </div>
</section>

<div class="page-shell">

  <!-- Alerts -->
  <div *ngIf="isLoading" class="status-bar loading">
    <mat-icon>hourglass_empty</mat-icon> Loading users…
  </div>
  <div *ngIf="error" class="status-bar error">
    <mat-icon>error</mat-icon> {{ error }}
  </div>
  <div *ngIf="resetPasswordSuccess" class="status-bar success">
    <mat-icon>check_circle</mat-icon> Password reset successfully!
  </div>

  <!-- Toolbar -->
  <div class="toolbar" *ngIf="!isLoading && users.length > 0">
    <div class="search-box">
      <mat-icon class="search-icon">search</mat-icon>
      <input type="text" [(ngModel)]="searchTerm" placeholder="Search by username, role, or email…" />
      <button class="search-clear" *ngIf="searchTerm" (click)="searchTerm = ''">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <a class="btn-create" routerLink="/user/add">
      <mat-icon>person_add</mat-icon> Add User
    </a>
  </div>

  <!-- Empty search state -->
  <div class="empty-state" *ngIf="!isLoading && users.length > 0 && filteredUsers.length === 0">
    <mat-icon>search_off</mat-icon>
    <p>No users match "<strong>{{ searchTerm }}</strong>"</p>
  </div>

  <!-- Table Card -->
  <div class="card" *ngIf="filteredUsers.length > 0">
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th class="sortable" (click)="toggleSort('user')">
              User
              <mat-icon class="sort-icon" *ngIf="sortColumn === 'user'">
                {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </mat-icon>
            </th>
            <th class="sortable" (click)="toggleSort('roles')">
              Roles
              <mat-icon class="sort-icon" *ngIf="sortColumn === 'roles'">
                {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
              </mat-icon>
            </th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let user of filteredUsers">
            <td class="cell-user">
              <div class="user-info">
                <img [src]="user.profilePhoto || 'images/profile-placeholder.png'" alt="Profile Photo" class="user-avatar-img"
                     (error)="$any($event.target).src = 'images/profile-placeholder.png'" />
                <div>
                  <div class="user-name">
                    {{ user.username }}
                    <span class="current-user-badge" *ngIf="isCurrentUser(user)">You</span>
                  </div>
                  <div class="user-email" *ngIf="user.email">{{ user.email }}</div>
                </div>
              </div>
            </td>
            <td class="cell-roles">
              <span class="role-badge" *ngFor="let role of user.roles">{{ role }}</span>
              <span class="no-roles" *ngIf="!user.roles.length">No roles</span>
            </td>
            <td class="cell-actions">
              <div class="actions-row">
                <button class="btn-action btn-primary" (click)="openEditDialog(user)">
                  <mat-icon>shield</mat-icon> Roles
                </button>
                <button *ngIf="!isCurrentUser(user)" class="btn-action btn-secondary" (click)="openEditPersonalInfoDialog(user)">
                  <mat-icon>edit</mat-icon> Edit
                </button>
                <button *ngIf="!isCurrentUser(user)" class="btn-action btn-warn" (click)="openResetPasswordDialog(user)">
                  <mat-icon>lock_reset</mat-icon> Reset
                </button>
                <button *ngIf="!isCurrentUser(user)" class="btn-action btn-danger" (click)="openRemoveDialog(user)">
                  <mat-icon>person_off</mat-icon> Deactivate
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

</div>

<!-- ── Modal: Edit Roles ── -->
<div class="modal-backdrop" *ngIf="showDialog" (click)="closeDialog()"></div>
<div class="modal-dialog" *ngIf="showDialog">
  <div class="modal-content">
    <div class="modal-header">
      <h3><mat-icon>shield</mat-icon> Edit Roles — {{ selectedUser?.username }}</h3>
      <button class="close-btn" (click)="closeDialog()"><mat-icon>close</mat-icon></button>
    </div>
    <div class="modal-body">
      <p class="modal-hint">Select the roles to assign or remove</p>
      <p class="self-role-note" *ngIf="selectedUser && isCurrentUser(selectedUser)">
        Role changes to your account take effect after signing in again.
      </p>
      <div class="role-list">
        <div *ngFor="let role of allRoles" class="role-item">
          <label class="role-label">
            <input type="checkbox"
                   [checked]="isRoleSelected(role)"
                   (change)="toggleRole(role)">
            <div class="role-details">
              <div class="role-name">{{ role.name }}</div>
              <div class="role-description">{{ role.description }}</div>
            </div>
          </label>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn-modal-cancel" (click)="closeDialog()">Cancel</button>
      <button class="btn-modal-save" (click)="saveRoles()">Save</button>
    </div>
  </div>
</div>

<!-- ── Modal: Deactivate User ── -->
<div class="modal-backdrop" *ngIf="showRemoveDialog" (click)="closeRemoveDialog()"></div>
<div class="modal-dialog" *ngIf="showRemoveDialog">
  <div class="modal-content">
    <div class="modal-header warn">
      <h3><mat-icon>warning</mat-icon> Deactivate User</h3>
      <button class="close-btn" (click)="closeRemoveDialog()"><mat-icon>close</mat-icon></button>
    </div>
    <div class="modal-body">
      <p>Are you sure you want to deactivate <strong>{{ userToRemove?.username }}</strong>?</p>
      <p class="warning-text"><mat-icon>info</mat-icon> They will lose all access to the system.</p>
    </div>
    <div class="modal-footer">
      <button class="btn-modal-cancel" (click)="closeRemoveDialog()">Cancel</button>
      <button class="btn-modal-danger" (click)="confirmRemoveUser()">Deactivate User</button>
    </div>
  </div>
</div>

<!-- ── Modal: Reset Password ── -->
<div class="modal-backdrop" *ngIf="showResetPasswordDialog" (click)="closeResetPasswordDialog()"></div>
<div class="modal-dialog" *ngIf="showResetPasswordDialog">
  <div class="modal-content">
    <div class="modal-header warn">
      <h3><mat-icon>lock_reset</mat-icon> Reset Password</h3>
      <button class="close-btn" (click)="closeResetPasswordDialog()"><mat-icon>close</mat-icon></button>
    </div>
    <div class="modal-body">
      <p>Set a new password for <strong>{{ userToResetPassword?.username }}</strong></p>
      <div class="form-group">
        <label for="newPassword">New Password</label>
        <div class="password-input-wrapper">
          <input [type]="showNewPassword ? 'text' : 'password'" id="newPassword" [(ngModel)]="newPassword" placeholder="Enter new password" />
          <button type="button" class="toggle-password-btn" (click)="showNewPassword = !showNewPassword">
            <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </div>
      </div>
      <div class="form-group">
        <label for="confirmPassword">Confirm Password</label>
        <div class="password-input-wrapper">
          <input [type]="showConfirmPassword ? 'text' : 'password'" id="confirmPassword" [(ngModel)]="confirmPassword" placeholder="Confirm new password" />
          <button type="button" class="toggle-password-btn" (click)="showConfirmPassword = !showConfirmPassword">
            <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </div>
      </div>
      <p class="error-text" *ngIf="resetPasswordError"><mat-icon>error</mat-icon> {{ resetPasswordError }}</p>
      <p class="warning-text"><mat-icon>info</mat-icon> This action is not reversible.</p>
    </div>
    <div class="modal-footer">
      <button class="btn-modal-cancel" (click)="closeResetPasswordDialog()">Cancel</button>
      <button class="btn-modal-danger" (click)="confirmResetPassword()">Reset Password</button>
    </div>
  </div>
</div>

<!-- ── Modal: Edit Personal Information ── -->
<div class="modal-backdrop" *ngIf="showEditPersonalInfoDialog" (click)="closeEditPersonalInfoDialog()"></div>
<div class="modal-dialog" *ngIf="showEditPersonalInfoDialog">
  <div class="modal-content">
    <div class="modal-header">
      <h3><mat-icon>edit</mat-icon> Edit Info — {{ selectedUser?.username }}</h3>
      <button class="close-btn" (click)="closeEditPersonalInfoDialog()"><mat-icon>close</mat-icon></button>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label for="firstname">First Name</label>
        <input type="text" id="firstname" name="firstname" [(ngModel)]="selectedUser!.firstname">
      </div>
      <div class="form-group">
        <label for="lastname">Last Name</label>
        <input type="text" id="lastname" name="lastname" [(ngModel)]="selectedUser!.lastname">
      </div>
      <div class="form-group">
        <label for="email">Email</label>
        <input type="email" id="email" name="email" [(ngModel)]="selectedUser!.email">
      </div>
      <div class="form-group">
        <label for="phoneNumber">Phone Number</label>
        <input type="text" id="phoneNumber" name="phoneNumber" [(ngModel)]="selectedUser!.phoneNumber">
      </div>
      <div class="form-group">
        <label for="job_title">Job Title</label>
        <input type="text" id="job_title" name="job_title" [(ngModel)]="selectedUser!.job_title">
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn-modal-cancel" (click)="closeEditPersonalInfoDialog()">Cancel</button>
      <button class="btn-modal-save" (click)="savePersonalInfo()">Save</button>
    </div>
  </div>
</div>
